import { createContext, useContext } from "react";
import type { SearchParams, SortParams } from "./types";

export type ManagerContextValue<T> = {
  data: T[];
  hasMore: boolean;
  loading: boolean;
  currentPage: number;
  totalCount: number | null;
  limit: number;
  selectedItem: T | null;
  isEditMode: boolean;
  dialogOpen: boolean;
  deleteDialogOpen: boolean;
  submitting: boolean;
  deleting: boolean;
  error: string | null;
  formValues: Record<string, unknown>;
  searchOpen: boolean;
  searchParams: SearchParams;
  hasSearch: boolean;
  sortParams: SortParams | null;
  sortableColumns: Set<string>;
  // Selection support
  allowSelection: boolean;
  selectedIds: Set<string | number>;
  allSelected: boolean;
  someSelected: boolean;
  selectionCount: number;
  toggleSelectAll: () => void;
  toggleSelectItem: (id: string | number) => void;
  clearSelection: () => void;
  setFormValue: (name: string, value: unknown) => void;
  openCreate: () => void;
  openEdit: (item: T) => void;
  openDelete: (item: T) => void;
  closeDialog: () => void;
  closeDeleteDialog: () => void;
  setSubmitting: (value: boolean) => void;
  setDeleting: (value: boolean) => void;
  setError: (error: string | null) => void;
  refresh: () => void;
  toggleSearch: () => void;
  setSearchText: (text: string) => void;
  setBinarySearch: (key: string, value: "yes" | "no" | undefined) => void;
  setFilter: (key: string, value: unknown) => void;
  setSort: (column: string) => void;
  registerSortableColumn: (column: string) => void;
};

// Generic contexts use `never` with type assertion at Provider - type safety enforced via useContext generic
export const ManagerContext = createContext<ManagerContextValue<never> | null>(
  null,
);

export function useManagerContext<T>() {
  const context = useContext(ManagerContext) as ManagerContextValue<T> | null;
  if (!context) {
    throw new Error("Manager components must be used within Manager.Root");
  }
  return context;
}
